import React, { useState, useRef } from 'react'
import classnames from 'classnames'
import ActionDropdown from '@SiteComponents/ActionDropdown'
import More from '@Images/projects_table_list_more.svg'
import * as timeago from 'timeago.js'

import {
  ISpeakerVoice,
} from '@SiteModules/SpeakerVoice/types'

import './SpeakerVoiceRow.scss'

interface SpeakerVoiceRowProps {
  readonly index: number
  readonly speakerVoice: ISpeakerVoice
  readonly selected: boolean
  changeSelectedSpeakerVoices(e: React.ChangeEvent<HTMLInputElement>, speakerVoiceId: string): void
  openRemoveSpeakerVoicePopup(speakerVoiceId: string): void
  handleSidePanelOpen(speakerVoice: ISpeakerVoice): void
}

const SpeakerVoiceRow: React.FC<SpeakerVoiceRowProps> = props => {
  const {
    index,
    speakerVoice,
    selected,
  } = props

  const {
    id,
    attributes,
  } = speakerVoice

  const {
    name,
    languageName,
    languageAccent,
    gender,
    description,
    private: isPrivate,
    thumbnailUrl,
    color,
    updatedAt,
    createdAt,
  } = attributes

  const [showDropdown, setShowDropdown] = useState(false)
  const checkboxRef = useRef<HTMLTableCellElement | null>(null)

  const moreElementId = `speaker_voice_row_more_${id}`
  const classNameForClickOutsideListenerMoreDropdown = `SpeakerVoicesTable__action-dropdown-element-${id}`

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown)
  }

  const onMouseEnter = () => {
    checkboxRef.current?.style?.setProperty('visibility', 'visible')
  }

  const onMouseLeave = () => {
    if (selected) { return }

    checkboxRef.current?.style?.setProperty('visibility', 'hidden')
  }

  const handleRemove = () => {
    props.openRemoveSpeakerVoicePopup(id)
    setShowDropdown(false)
  }

  const handleSpeakerVoiceSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.changeSelectedSpeakerVoices(e, id)
  }

  const handleClick = () => {
    props.handleSidePanelOpen(speakerVoice)
  }

  const renderCheckbox = () => {
    return (
      <label className="SpeakerVoicesTable__checkbox SpeakerVoiceRow__checkbox">
        <input
          type="checkbox"
          onChange={handleSpeakerVoiceSelection}
          checked={selected}
        />
        <div className="SpeakerVoiceRow__checkmark" />
      </label>
    )
  }

  const renderThumbnail = () => {
    const shouldShowColoredThumbnail = !thumbnailUrl || thumbnailUrl.includes('thumbnail-icon.png')

    return (
      <React.Fragment>
        {shouldShowColoredThumbnail ? (
          <div
            style={{ backgroundColor: color }}
            className="SpeakerVoiceRow__thumbnail-image"
          />
        ) : (
          <img
            className="SpeakerVoiceRow__thumbnail-image"
            src={thumbnailUrl}
            alt="Voice thumbnail"
          />
        )}
      </React.Fragment>
    )
  }

  const renderName = () => {
    return (
      <div className="SpeakerVoiceRow__name">
        {name}
      </div>
    )
  }

  const actionDropdownList = (
    <ul>
      <li
        className="SpeakerVoicesTable__action-dropdown-item"
        onClick={handleRemove}
      >
        <div className="SpeakerVoicesTable__action-dropdown-item-text">
          Remove
        </div>
      </li>
    </ul>
  )

  const renderActionDropdown = () => {
    return (
      <ActionDropdown
        showDropdown={showDropdown}
        dropdownList={actionDropdownList}
        classNamesForListContainer="SpeakerVoicesTable__action-dropdown"
        classNameForClickOutsideListener={classNameForClickOutsideListenerMoreDropdown}
        classNameOfScrollContainer="SpeakerVoicesTableList"
        idOfDropdownElement={moreElementId}
        idOfPortalContainer="voiceStudio"
        dropdownOffsetX={45}
        dropdownOffsetY={0}
        dropdownWidth={209} // same width as SpeakerVoicesTable__action-dropdown
        setShowDropdown={setShowDropdown}
      />
    )
  }

  return (
    <tr
      className={classnames(
        { 'SpeakerVoiceRow__even': index % 2 === 0 },
        { 'SpeakerVoiceRow__uneven': index % 2 === 1 }
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <td className="SpeakerVoicesTableList__check SpeakerVoiceRow__check" ref={checkboxRef}>
        {renderCheckbox()}
      </td>
      <td className="SpeakerVoicesTableList__thumbnail-name SpeakerVoiceRow__col" onClick={handleClick}>
        <div className="SpeakerVoiceRow__thumbnail-name-wrapper">
          {renderThumbnail()}
          {renderName()}
        </div>
      </td>
      <td className="SpeakerVoicesTableList__description SpeakerVoiceRow__col" onClick={handleClick}>
        {description}
      </td>
      <td className="SpeakerVoicesTableList__language SpeakerVoiceRow__col" onClick={handleClick}>
        {languageName}{languageAccent ? ` (${languageAccent})` : ''}
      </td>
      <td className="SpeakerVoicesTableList__gender SpeakerVoiceRow__col" onClick={handleClick}>
        {gender}
      </td>
      <td className="SpeakerVoicesTableList__edited SpeakerVoiceRow__col" onClick={handleClick}>
        {timeago.format(updatedAt)}
      </td>
      <td className="SpeakerVoicesTableList__private SpeakerVoiceRow__col" onClick={handleClick}>
        {isPrivate ? 'Yes' : 'No'}
      </td>
      <td className="SpeakerVoicesTableList__date-of-creation SpeakerVoiceRow__col" onClick={handleClick}>
        {createdAt}
      </td>
      <td className="SpeakerVoicesTableList__more SpeakerVoiceRow__more">
        <div
          className={`SpeakerVoiceRow__more-wrapper ${classNameForClickOutsideListenerMoreDropdown}`}
          id={moreElementId}
        >
          <More
            onClick={toggleDropdown}
          />
          {showDropdown && renderActionDropdown()}
        </div>
      </td>
    </tr>
  )
}

export default SpeakerVoiceRow
